/**
 * Client Success → Coverage (ADR-0101).
 *
 * The supply-side surface: a US choropleth shaded by coverage depth for the
 * selected case types, a fixed detail card (who covers a state, in priority
 * order, at what fee %), and a gaps strip (where we're absent or one firm
 * deep, plus the missing-fee hygiene count).
 *
 * Shading is WEAKEST-LINK across the selected chips — a state shades by its
 * thinnest selected type, so multi-select answers "where am I covered for
 * everything selected". Depth counts active rows only; paused rows (row- or
 * firm-level) show muted in the card but never shade the map.
 *
 * The nine small eastern jurisdictions (incl. DC, which has no map path at
 * all) get equal-size squares in a strip beside the map — those are their
 * real hover/click targets; the map paths are too small to hit reliably.
 *
 * Hover updates the card; click pins it (click again, or click another
 * state, to unpin/repin). The card is always mounted so the layout never
 * shifts. All derivations live in `coverage-derive.ts` (pure, unit-tested).
 *
 * Spec: engineering/command-center/docs/coverage-map-plan.md (evicted 2026-08-29, #775 round 10)
 */

import { useState } from "react";
import type { CoverageRow } from "@command-center/shared";
import { UsMap } from "@/components/us-map";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useCoverage } from "@/lib/use-coverage";
import {
  caseTypesPresent,
  computeGaps,
  depthByState,
  detailForState,
  fillForDepth,
  formatFee,
  type Depth,
} from "@/lib/coverage-derive";
import { US_STATE_NAMES } from "@/assets/us-states-paths";

/** Chip selected on first load when present (the patchy, decision-rich map). */
const DEFAULT_CASE_TYPE = "Personal injury";

/** The map's untargetable small jurisdictions — the strip squares are their
 *  hover/click affordance. North → south, DC last. */
const SMALL_STATES = [
  "VT",
  "NH",
  "MA",
  "CT",
  "RI",
  "NJ",
  "DE",
  "MD",
  "DC",
] as const;

const DEPTH_LEGEND: { depth: Depth; label: string }[] = [
  { depth: 0, label: "none" },
  { depth: 1, label: "1 firm" },
  { depth: 2, label: "2 firms" },
  { depth: 3, label: "3+ firms" },
];

export function CoveragePage() {
  const coverage = useCoverage();
  // null until the operator first toggles a chip — the default derives from
  // the data (PI when present), which isn't known until the fetch resolves.
  const [selectedOverride, setSelectedOverride] = useState<Set<string> | null>(
    null,
  );
  const [hovered, setHovered] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(null);

  if (coverage.isLoading) {
    return (
      <Shell>
        <div className="p-8 text-sm text-plane-muted">Loading…</div>
      </Shell>
    );
  }

  if (coverage.isError) {
    return (
      <Shell>
        <div className="p-8 text-sm text-error" data-testid="coverage-error">
          Couldn't load coverage. It will retry automatically.
        </div>
      </Shell>
    );
  }

  const rows = coverage.data?.rows ?? [];
  if (rows.length === 0) {
    return (
      <Shell>
        <div
          className="p-8 text-sm text-plane-muted"
          data-testid="coverage-empty"
        >
          No coverage rows yet — the Coverage table in Airtable is empty.
        </div>
      </Shell>
    );
  }

  const caseTypes = caseTypesPresent(rows);
  const defaultType = caseTypes.includes(DEFAULT_CASE_TYPE)
    ? DEFAULT_CASE_TYPE
    : caseTypes[0];
  const selected =
    selectedOverride ??
    new Set<string>(defaultType !== undefined ? [defaultType] : []);
  const selectedList = caseTypes.filter((t) => selected.has(t));

  const depths = depthByState(rows, selectedList);
  const gaps = computeGaps(rows, selectedList);
  const displayed = pinned ?? hovered;

  function toggleType(type: string): void {
    const next = new Set(selected);
    if (next.has(type)) next.delete(type);
    else next.add(type);
    setSelectedOverride(next);
  }

  function selectState(code: string): void {
    setPinned((prev) => (prev === code ? null : code));
  }

  return (
    <Shell>
      <div
        className="mb-4 flex flex-wrap gap-1 border-b"
        role="tablist"
        data-testid="coverage-filter"
      >
        {caseTypes.map((type) => (
          <button
            key={type}
            type="button"
            role="tab"
            aria-selected={selected.has(type)}
            onClick={() => toggleType(type)}
            data-testid={`coverage-filter-${type}`}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-sm transition-colors",
              selected.has(type)
                ? "border-active-edge font-medium text-plane-foreground"
                : "border-transparent text-plane-muted hover:text-plane-foreground",
            )}
          >
            {type}
          </button>
        ))}
      </div>

      <div className="flex gap-6">
        <div className="min-w-0 flex-1">
          {selectedList.length === 0 ? (
            <div
              className="p-8 text-sm text-plane-muted"
              data-testid="coverage-no-selection"
            >
              Select a case type to shade the map.
            </div>
          ) : null}
          <UsMap depths={depths} onHover={setHovered} onSelect={selectState} />
          <div className="mt-2 flex items-center gap-4 text-xs text-plane-muted">
            {DEPTH_LEGEND.map(({ depth, label }) => (
              <span key={depth} className="flex items-center gap-1.5">
                <span
                  className="inline-block h-3 w-3 rounded-sm border"
                  style={{ backgroundColor: fillForDepth(depth) }}
                />
                {label}
              </span>
            ))}
            <span>· shaded by weakest selected type</span>
          </div>
        </div>

        <div className="flex w-72 shrink-0 flex-col gap-4">
          <div>
            <div className="mb-1 text-xs text-plane-muted">
              Small states
            </div>
            <div className="flex flex-wrap gap-1" data-testid="coverage-strip">
              {SMALL_STATES.map((code) => (
                <button
                  key={code}
                  type="button"
                  data-testid={`coverage-strip-${code}`}
                  onMouseEnter={() => setHovered(code)}
                  onClick={() => selectState(code)}
                  className="flex h-8 w-8 items-center justify-center rounded-sm border text-[10px] font-medium"
                  style={{ backgroundColor: fillForDepth(depths[code] ?? 0) }}
                >
                  <span className="rounded-sm bg-background/70 px-0.5">
                    {code}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <StateDetailCard
            state={displayed}
            pinned={pinned !== null}
            rows={rows}
            selectedTypes={selectedList}
          />
        </div>
      </div>

      {selectedList.length > 0 ? (
        <Card className="mt-6 p-4 text-sm" data-testid="coverage-gaps">
          <div className="mb-1 font-medium">
            Gaps — {selectedList.join(" + ")}
          </div>
          <div className="text-muted-foreground">
            <div data-testid="coverage-gaps-none">
              No coverage ({gaps.noCoverage.length}):{" "}
              {gaps.noCoverage.length > 0 ? gaps.noCoverage.join(", ") : "—"}
            </div>
            <div data-testid="coverage-gaps-one">
              One firm deep ({gaps.oneDeep.length}):{" "}
              {gaps.oneDeep.length > 0 ? gaps.oneDeep.join(", ") : "—"}
            </div>
            <div data-testid="coverage-gaps-fee">
              Active rows missing fee %: {gaps.missingFeeCount}
            </div>
          </div>
        </Card>
      ) : null}
    </Shell>
  );
}

function StateDetailCard({
  state,
  pinned,
  rows,
  selectedTypes,
}: {
  state: string | null;
  pinned: boolean;
  rows: CoverageRow[];
  selectedTypes: string[];
}) {
  // Always mounted so the layout never shifts between idle and hover.
  if (state === null) {
    return (
      <Card className="p-4 text-sm text-muted-foreground" data-testid="coverage-detail">
        Hover a state to see its firms; click to pin.
      </Card>
    );
  }

  const details = detailForState(rows, state, selectedTypes);
  return (
    <Card className="p-4 text-sm" data-testid="coverage-detail">
      <div className="mb-2 flex items-baseline justify-between">
        <span className="font-medium" data-testid="coverage-detail-state">
          {US_STATE_NAMES[state] ?? state}
        </span>
        {pinned ? (
          <span className="text-xs text-muted-foreground">
            pinned — click the state to unpin
          </span>
        ) : null}
      </div>
      {details.map(({ caseType, rows: detailRows }) => (
        <div key={caseType} className="mb-3 last:mb-0">
          <div className="text-xs font-medium text-muted-foreground">
            {caseType}
          </div>
          {detailRows.length === 0 ? (
            <div className="text-muted-foreground">No firms</div>
          ) : (
            <ol className="mt-0.5">
              {detailRows.map((r) => (
                <li
                  key={r.id}
                  className={cn(
                    "tabular-nums",
                    r.paused && "text-muted-foreground",
                  )}
                >
                  {r.priority ?? "—"}. {r.firmName} — {formatFee(r.feeHfPct)}
                  {r.paused ? (
                    <span className="ml-1.5 rounded-sm border px-1 text-[10px] uppercase">
                      paused
                    </span>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </div>
      ))}
    </Card>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section
      className="mx-auto max-w-5xl p-6"
      data-testid="firm-success-coverage"
    >
      {children}
    </section>
  );
}
